import { ref, onUnmounted, onMounted } from "vue";
import CryptoJS from "crypto-js";
// Type definitions
export interface TTSOptions {
    apiUrl?: string;
    defaultModel?: string;
    defaultVoice?: string;
    defaultSpeed?: number;
}

export interface NormalizationOptions {
    normalize: boolean;
    unit_normalization: boolean;
    url_normalization: boolean;
    email_normalization: boolean;
    optional_pluralization_normalization: boolean;
    phone_normalization: boolean;
}

export interface TTSRequestPayload {
    model: string;
    input: string;
    voice: string;
    response_format: string;
    speed: number;
    stream: boolean;
    return_download_link: boolean;
    normalization_options: NormalizationOptions;
}

// Default options
const DEFAULT_OPTIONS: TTSOptions = {
    apiUrl: "https://tts.magly.cn/v1/audio/speech/en",
    defaultModel: "kokoro",
    defaultVoice: "af_heart",
    defaultSpeed: 1
};

// Default normalization options
const DEFAULT_NORMALIZATION: NormalizationOptions = {
    normalize: true,
    unit_normalization: false,
    url_normalization: true,
    email_normalization: true,
    optional_pluralization_normalization: true,
    phone_normalization: true
};


// Utility function for payload encryption
function encryptPayload(data: any): string {
    const jsonString = JSON.stringify(data);
    var derived_key = CryptoJS.enc.Base64.parse("LefjQ2pEXmiy/nNZvEJ43i8hJuaAnzbA1Cbn1hOuAgA=")
    var iv = CryptoJS.enc.Utf8.parse("1020304050607080");
    var test = CryptoJS.AES.encrypt(jsonString, derived_key, { iv: iv, mode: CryptoJS.mode.CBC }).toString();
    return test;
}

export function useTTS(options: TTSOptions = {}) {
    // Merge provided options with defaults
    const config = { ...DEFAULT_OPTIONS, ...options };

    const audioElement = ref<HTMLAudioElement | null>(null);
    const textToSpeak = ref("");
    const isPlaying = ref(false);
    const error = ref("");
    const isLoading = ref(false);

    onMounted(() => {
        audioElement.value = new Audio();
    });

    const stopPlayback = () => {
        if (!audioElement.value) return;
        audioElement.value.pause();
        audioElement.value.currentTime = 0;
        isPlaying.value = false;
    };

    const handlePlay = async (
        text?: string,
        model = config.defaultModel,
        voice = config.defaultVoice,
        speed = config.defaultSpeed
    ) => {
        // If text is provided directly, use it; otherwise use the ref value
        const textContent = text || textToSpeak.value;

        if (!textContent.trim() || isPlaying.value || !audioElement.value) {
            return;
        }

        const audio = audioElement.value;
        error.value = "";
        isPlaying.value = true;
        isLoading.value = true;

        try {
            const payload: TTSRequestPayload = {
                model: model || "kokoro",
                input: textContent,
                voice: voice || "af_heart",
                response_format: "mp3",
                speed: speed || 1,
                stream: true,
                return_download_link: false,
                normalization_options: DEFAULT_NORMALIZATION
            };

            // Encrypt the payload before sending
            const encryptedPayload = encryptPayload(payload);

            // Create URL with query parameter
            const urlWithParams = new URL(config.apiUrl!);
            urlWithParams.searchParams.append('request', encryptedPayload);

            const response = await fetch(urlWithParams, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "audio/mpeg",
                    "X-Encrypted-Data": "true"
                }
                // body is removed since we're using query parameters
            });

            if (!response.ok) {
                const errorText = await response.text();
                try {
                    const errorData = JSON.parse(errorText);
                    throw new Error(
                        errorData.message || `Server error: ${response.status}`
                    );
                } catch (e) {
                    throw new Error(`Server error: ${response.status} - ${errorText}`);
                }
            }

            const contentType = response.headers.get("content-type");
            if (!contentType?.includes("audio/")) {
                throw new Error(`Invalid content type: ${contentType}`);
            }

            if (!response.body) {
                throw new Error("No response body received");
            }

            const reader = response.body.getReader();
            let initialPlaybackStarted = false;
            const pendingChunks: Uint8Array[] = [];
            let processingBuffer = false;

            // Create a MediaSource for seamless streaming
            const mediaSource = new MediaSource();
            const mediaSourceUrl = URL.createObjectURL(mediaSource);
            audio.src = mediaSourceUrl;
            let sourceBuffer: SourceBuffer | null = null;

            // Function to process the next chunk in the queue
            const processNextChunk = async () => {
                if (!sourceBuffer || pendingChunks.length === 0 || processingBuffer) {
                    return;
                }

                processingBuffer = true;

                try {
                    const chunk = pendingChunks.shift();
                    if (chunk && !sourceBuffer.updating) {
                        sourceBuffer.appendBuffer(chunk);

                        // Wait for this append to complete before processing the next chunk
                        await new Promise<void>((resolve) => {
                            const updateEndHandler = () => {
                                sourceBuffer!.removeEventListener("updateend", updateEndHandler);
                                resolve();
                            };
                            sourceBuffer!.addEventListener("updateend", updateEndHandler);
                        });

                        // Start playback if not already started
                        if (!initialPlaybackStarted) {
                            try {
                                await audio.play();
                                initialPlaybackStarted = true;
                                isLoading.value = false;
                            } catch (playError) {
                                console.warn("Playback not ready yet:", playError);
                            }
                        }
                    }
                } finally {
                    processingBuffer = false;

                    // Process next chunk if there are more
                    if (pendingChunks.length > 0) {
                        processNextChunk();
                    }
                }
            };

            mediaSource.addEventListener("sourceopen", async () => {
                try {
                    sourceBuffer = mediaSource.addSourceBuffer("audio/mpeg");
                    sourceBuffer.mode = "sequence";

                    // Process chunks as they arrive
                    while (true) {
                        const { done, value } = await reader.read();

                        if (done) {
                            // Wait for all chunks to be processed
                            while (pendingChunks.length > 0 || processingBuffer) {
                                await new Promise((resolve) => setTimeout(resolve, 50));
                            }

                            if (
                                sourceBuffer &&
                                !sourceBuffer.updating &&
                                mediaSource.readyState === "open"
                            ) {
                                mediaSource.endOfStream();
                            }
                            break;
                        }

                        // Add chunk to pending queue
                        pendingChunks.push(value);

                        // Start processing if not already
                        if (!processingBuffer) {
                            processNextChunk();
                        }
                    }
                } catch (streamError) {
                    console.error("Streaming error:", streamError);
                    error.value =
                        streamError instanceof Error
                            ? streamError.message
                            : "Error during audio streaming";
                }
            });

            // Error handling for MediaSource
            mediaSource.addEventListener("error", (e) => {
                console.error("MediaSource error:", e);
                error.value = "MediaSource error occurred";
                isPlaying.value = false;
                isLoading.value = false;
            });

            audio.onended = () => {
                isPlaying.value = false;
                if (mediaSource.readyState === "open") {
                    try {
                        mediaSource.endOfStream();
                    } catch (e) {
                        console.warn("Could not call endOfStream:", e);
                    }
                }
                // Cleanup MediaSource URL when done
                URL.revokeObjectURL(mediaSourceUrl);
            };

            audio.onerror = (e) => {
                const audioError = audio.error;
                if (audioError) {
                    console.error("Audio error:", {
                        code: audioError.code,
                        message: audioError.message,
                    });
                    error.value = `Playback error: ${audioError.message || "Unknown error"
                        }`;
                }
                isPlaying.value = false;
                isLoading.value = false;
                // Cleanup MediaSource URL on error
                URL.revokeObjectURL(mediaSourceUrl);
            };
        } catch (err) {
            console.error("Error in handlePlay:", err);
            error.value =
                err instanceof Error ? err.message : "Failed to generate speech";
            isPlaying.value = false;
            isLoading.value = false;
        }
    };

    // Cleanup on component unmount
    onUnmounted(() => {
        if (!audioElement.value) return;
        audioElement.value.pause();
        audioElement.value.onended = null;
        audioElement.value.onerror = null;
        if (audioElement.value.src) {
            URL.revokeObjectURL(audioElement.value.src);
            audioElement.value.src = "";
        }
    });

    // Return the composable API
    return {
        textToSpeak,
        isPlaying,
        isLoading,
        error,
        handlePlay,
        stopPlayback
    };
}